import { useEffect, useState } from 'react'
import { doGet2 } from '../../services/common'

// One page is plenty — sites have a handful of taxonomies, and the default
// page size (20) would silently drop the rest.
const TAXONOMY_PAGE_SIZE = 100

export type OfferTag = {
  id: string | number
  label: string
  param?: string | number
  noSelect?: boolean
}

type TaxonomyResponse = {
  content?: Array<{
    slug?: string
    label?: string
    tags?: Array<{ id: number; label: string; hidden?: boolean }>
  }>
}

/*
  Every taxonomy on the site becomes a labelled group in the FILTER dropdown.
  Beaker2 lets an author tag a promo item from any of the site's taxonomies, so
  scoping this to one hardcoded slug (it used to be 'brand', carried over from
  the fifaunilever page) left the dropdown empty on every other site.

  The API ANDs across taxonomies and ORs within one, which is what you want:
  Brand: A or B, and Category: C.
*/
const buildDropdownItems = (data: TaxonomyResponse): OfferTag[] =>
  (data.content ?? []).flatMap(group => {
    const tags = (group.tags ?? []).filter(tag => !tag.hidden)
    if (tags.length === 0) return []
    return [
      {
        id: `group-${group.slug ?? group.label}`,
        label: group.label ?? group.slug ?? '',
        noSelect: true,
      },
      ...tags.map(tag => ({ id: tag.id, label: tag.label, param: tag.id })),
    ]
  })

export const useOfferTags = () => {
  const [offerTags, setOfferTags] = useState<OfferTag[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    const loadTags = async () => {
      try {
        setIsLoading(true)
        const response = await doGet2('/taxonomy', {
          size: TAXONOMY_PAGE_SIZE,
        })
        const data: TaxonomyResponse = await response.json()

        if (cancelled) return

        if (data?.content) {
          setOfferTags(buildDropdownItems(data))
        } else {
          console.warn('[OffersSection] Unexpected response from taxonomy API')
          setError('Failed to load tags')
        }
      } catch (err) {
        if (cancelled) return
        console.warn('[OffersSection] Error loading offer tags:', err)
        setError('Failed to load tags')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    loadTags()

    return () => {
      cancelled = true
    }
  }, [])

  return { offerTags, isLoading, error }
}
